import React, { useEffect, useMemo, useState } from 'react';
import { Alert, AlertTitle, Box, Button, Collapse, Link as MuiLink } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { tokens } from '../theme/tokens';

const STORAGE_PREFIX = 'atutein_dismissed_system_notice:';

/**
 * Temporal staff banner for one-off system notices (e.g. legacy member import).
 * - Globally disable via Settings / "Hide for everyone"
 * - Per-user dismiss via localStorage keyed by notice id
 */
export default function SystemNoticeBanner() {
  const { staff } = useAuth();
  const [notice, setNotice] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [hiding, setHiding] = useState(false);

  const perms = staff?.permissions || [];
  const canManage = perms.includes('all') || perms.includes('members');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/app-settings');
        const n = res.data?.data?.system_notice;
        if (cancelled || !n) return;
        setNotice(n);
        if (n.id) {
          const key = STORAGE_PREFIX + n.id;
          setDismissed(localStorage.getItem(key) === '1');
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const visible = useMemo(() => {
    if (!notice || !notice.enabled) return false;
    if (!notice.title && !notice.message) return false;
    if (dismissed) return false;
    return true;
  }, [notice, dismissed]);

  const handleDismiss = () => {
    if (notice?.id) {
      localStorage.setItem(STORAGE_PREFIX + notice.id, '1');
    }
    setDismissed(true);
  };

  const handleDisableGlobal = async () => {
    setHiding(true);
    try {
      await api.put('/app-settings', { system_notice_enabled: false });
      setNotice((prev) => (prev ? { ...prev, enabled: false } : prev));
    } catch {
      /* ignore */
    } finally {
      setHiding(false);
    }
  };

  if (!visible) return null;

  return (
    <Collapse in={visible}>
      <Alert
        severity="info"
        variant="outlined"
        onClose={handleDismiss}
        sx={{
          mb: 2.5,
          borderColor: tokens.primary,
          bgcolor: tokens.primarySoft || 'rgba(30, 92, 70, 0.08)',
          '& .MuiAlert-message': { width: '100%' },
        }}
      >
        {notice.title ? <AlertTitle sx={{ fontWeight: 700 }}>{notice.title}</AlertTitle> : null}
        <Box sx={{ fontSize: 14, color: tokens.ink, mb: 1.25 }}>{notice.message}</Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          {notice.cta_path ? (
            <Button
              component={RouterLink}
              to={notice.cta_path}
              size="small"
              variant="contained"
              sx={{ textTransform: 'none', bgcolor: tokens.primary, '&:hover': { bgcolor: tokens.primary } }}
            >
              {notice.cta_label || 'View'}
            </Button>
          ) : null}
          <Button size="small" onClick={handleDismiss} sx={{ textTransform: 'none' }}>
            Dismiss
          </Button>
          {canManage ? (
            <MuiLink
              component="button"
              type="button"
              underline="hover"
              disabled={hiding}
              onClick={handleDisableGlobal}
              sx={{ fontSize: 13, color: tokens.muted, ml: { sm: 'auto' } }}
            >
              {hiding ? 'Hiding…' : 'Hide for everyone'}
            </MuiLink>
          ) : null}
        </Box>
      </Alert>
    </Collapse>
  );
}
